
import { safeNumberConversion, isValidNumber } from './transactionSafeCalculations';

/**
 * Dedicated seller credit calculation utilities with enhanced debugging and validation
 */

export interface SellerCreditCalculationResult {
  requestedPercentage: number;
  homePrice: number;
  requestedDollarAmount: number;
  effectiveDollarAmount: number;
  isValid: boolean;
  debugInfo: {
    step1_percentageInput: number;
    step2_homePrice: number;
    step3_dollarCalculation: number;
    step4_closingCostLimit: number;
    step5_finalAmount: number;
  };
}

/**
 * Calculate seller credit with comprehensive debugging and improved validation
 */
export const calculateSellerCreditAmount = (
  sellerCreditPercentage: number,
  homePrice: number,
  totalClosingCosts: number
): SellerCreditCalculationResult => {
  // Step 1: Validate and convert percentage input with stricter bounds
  const safePercentage = safeNumberConversion(sellerCreditPercentage, 0);
  const safeHomePrice = safeNumberConversion(homePrice, 0);
  const safeClosingCosts = safeNumberConversion(totalClosingCosts, 0);
  
  console.log('Seller Credit Debug - Input Values:', {
    sellerCreditPercentage,
    homePrice,
    totalClosingCosts,
    safePercentage,
    safeHomePrice,
    safeClosingCosts
  });
  
  // Validate minimum thresholds to prevent rounding errors
  if (safePercentage < 0.01) { // Less than 0.01% is effectively zero
    console.log('Seller Credit Debug - Percentage too small, returning zero');
    return {
      requestedPercentage: 0,
      homePrice: safeHomePrice,
      requestedDollarAmount: 0,
      effectiveDollarAmount: 0,
      isValid: true,
      debugInfo: {
        step1_percentageInput: safePercentage,
        step2_homePrice: safeHomePrice,
        step3_dollarCalculation: 0,
        step4_closingCostLimit: safeClosingCosts,
        step5_finalAmount: 0
      }
    };
  }
  
  // Step 2: Calculate dollar amount from percentage
  const requestedDollarAmount = Math.round((safePercentage / 100) * safeHomePrice);
  
  // Step 3: Apply closing cost ceiling (credit cannot exceed closing costs)
  const effectiveDollarAmount = Math.max(0, Math.min(requestedDollarAmount, safeClosingCosts));
  
  // Step 4: Validation
  const isValid = isValidNumber(safePercentage) && 
                  isValidNumber(safeHomePrice) && 
                  isValidNumber(safeClosingCosts) &&
                  safeHomePrice > 0 &&
                  safePercentage >= 0;
  
  const result: SellerCreditCalculationResult = {
    requestedPercentage: safePercentage,
    homePrice: safeHomePrice,
    requestedDollarAmount,
    effectiveDollarAmount,
    isValid,
    debugInfo: {
      step1_percentageInput: safePercentage,
      step2_homePrice: safeHomePrice,
      step3_dollarCalculation: requestedDollarAmount,
      step4_closingCostLimit: safeClosingCosts,
      step5_finalAmount: effectiveDollarAmount
    }
  };
  
  console.log('Seller Credit Debug - Calculation Result:', result);
  
  return result;
};

/**
 * Enhanced seller credit input validation with stricter bounds
 */
export const validateSellerCreditInput = (
  value: any,
  homePrice: number
): { isValid: boolean; message: string; safeValue: number } => {
  const safeHomePrice = safeNumberConversion(homePrice, 0);
  
  // Handle empty or null input
  if (value === null || value === undefined || value === '') {
    return {
      isValid: true,
      message: '',
      safeValue: 0
    };
  }
  
  // Convert to number
  const numericValue = parseFloat(String(value));
  
  // Check if conversion resulted in valid number
  if (isNaN(numericValue)) {
    return {
      isValid: false,
      message: 'Please enter a valid percentage',
      safeValue: 0
    };
  }
  
  // Check for negative values
  if (numericValue < 0) {
    return {
      isValid: false,
      message: 'Seller credit cannot be negative',
      safeValue: 0
    };
  }
  
  // Check for unreasonably high values
  if (numericValue > 10) {
    return {
      isValid: false,
      message: 'Seller credit cannot exceed 10%',
      safeValue: 10
    };
  }
  
  // Check for very small values that cause rounding issues
  if (numericValue > 0 && numericValue < 0.01) {
    return {
      isValid: false,
      message: 'Minimum seller credit is 0.01%',
      safeValue: 0
    };
  }
  
  // Check for home price availability
  if (safeHomePrice <= 0) {
    return {
      isValid: false,
      message: 'Please enter a home price first',
      safeValue: numericValue
    };
  }
  
  return {
    isValid: true,
    message: '',
    safeValue: numericValue
  };
};
